import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import VideoCRUDService from '../services/VideoCRUDService';

const VideoProcessingContext = createContext();

export const useVideoProcessing = () => useContext(VideoProcessingContext);

export const VideoProcessingProvider = ({ children }) => {
  // Use an empty array for production
  const [processingVideos, setProcessingVideos] = useState([]);
  
  // Track all polling intervals to clean them up
  const pollingIntervalsRef = useRef({});
  
  // Clean up function to remove completed videos
  const cleanupCompletedVideos = useCallback(() => {
    setProcessingVideos(prev => 
      prev.filter(video => {
        // Keep videos that are still processing
        const isComplete = 
          (video.compression === 'COMPLETED' || video.compression === 'FAILED') && 
          (video.translation === 'COMPLETED' || video.translation === 'FAILED' || video.translation === 'SKIPPED');
        
        // If complete for more than 5 minutes, remove it
        if (isComplete && video.lastUpdated) {
          const fiveMinutesAgo = new Date();
          fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);
          return new Date(video.lastUpdated) > fiveMinutesAgo;
        }
        
        return true;
      })
    );
  }, []);
  
  // Poll for a specific video's status
  const pollVideoStatus = useCallback(async (videoId) => {
    console.log(`Polling status for video ${videoId}`);
    
    try {
      const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      const authToken = JSON.parse(localStorage.getItem('authToken') || '""');
      
      if (!authUser?.email) {
        console.log('No authenticated user, skipping poll');
        return;
      }
      
      const response = await VideoCRUDService.getVideoProcessingStatus(
        videoId,
        authUser.email,
        authToken
      );
      
      if (response && response.data) {
        console.log(`Got status update for ${videoId}:`, response.data);
        
        // Update just this video in the state
        setProcessingVideos(prev => 
          prev.map(video => 
            video.id === videoId ? {
              ...video,
              ...response.data,
              lastUpdated: new Date()
            } : video
          )
        );
        
        // Check if this video is completed and can be removed from polling
        const videoData = response.data;
        if ((videoData.compression === 'COMPLETED' || videoData.compression === 'FAILED') && 
            (videoData.translation === 'COMPLETED' || videoData.translation === 'FAILED' || videoData.translation === 'SKIPPED')) {
          console.log(`Video ${videoId} is complete, stopping polling`);
          clearInterval(pollingIntervalsRef.current[videoId]);
          delete pollingIntervalsRef.current[videoId];
        }
      }
    } catch (err) {
      console.error(`Error polling status for video ${videoId}:`, err);
    }
  }, []);
  
  // Start polling for a specific video
  const startPolling = useCallback((videoId) => {
    console.log(`Starting polling for video ${videoId}`);
    
    // Clear any existing polling for this video
    if (pollingIntervalsRef.current[videoId]) {
      clearInterval(pollingIntervalsRef.current[videoId]);
    }
    
    // Poll immediately first
    pollVideoStatus(videoId);
    
    // Then poll every 5 seconds
    const intervalId = setInterval(() => pollVideoStatus(videoId), 60000);
    pollingIntervalsRef.current[videoId] = intervalId;
    
    return () => {
      clearInterval(intervalId);
      delete pollingIntervalsRef.current[videoId];
    };
  }, [pollVideoStatus]);
  
  // Add a new video to track
  const trackVideo = useCallback((videoId, videoName, skipTranslation = false) => {
    console.log(`Tracking new video: ${videoId} - ${videoName}`);
    
    if (!videoId) {
      console.error('Cannot track video: No videoId provided');
      return;
    }
    
    // First, check if we're already tracking this video
    setProcessingVideos(prev => {
      const alreadyTracking = prev.some(v => v.id === videoId);
      if (alreadyTracking) {
        console.log(`Already tracking ${videoId}, skipping`);
        return prev;
      }
      
      console.log(`Adding ${videoId} to tracking list`);
      const newVideo = {
        id: videoId,
        name: videoName || 'Unnamed Video',
        skipTranslation: skipTranslation,
        addedAt: new Date(),
        compression: 'PENDING',
        translation: skipTranslation ? 'SKIPPED' : 'PENDING',
        expanded: false, // Collapsed by default
        languages: []
      };
      
      // Start polling for this video's status
      setTimeout(() => startPolling(videoId), 500);
      
      return [...prev, newVideo];
    });
  }, [startPolling]);
  
  // Remove a video from tracking
  const removeVideo = useCallback((videoId) => {
    console.log(`Removing video ${videoId} from tracking`);
    
    // Clear any polling intervals for this video
    if (pollingIntervalsRef.current[videoId]) {
      clearInterval(pollingIntervalsRef.current[videoId]);
      delete pollingIntervalsRef.current[videoId];
    }
    
    setProcessingVideos(prev => prev.filter(v => v.id !== videoId));
  }, []);
  
  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(pollingIntervalsRef.current).forEach(interval => {
        clearInterval(interval);
      });
    };
  }, []);
  
  // Run cleanup periodically
  useEffect(() => {
    const cleanupInterval = setInterval(cleanupCompletedVideos, 60000); // Check every minute
    return () => clearInterval(cleanupInterval);
  }, [cleanupCompletedVideos]);
  
  // Toggle expanded state for a video
  const toggleExpanded = useCallback((videoId) => {
    setProcessingVideos(prev => 
      prev.map(video => 
        video.id === videoId 
          ? { ...video, expanded: !video.expanded }
          : video
      )
    );
  }, []);
  
  // Calculate completion percentage
  const getCompletionPercentage = useCallback((video) => {
    let total = 2; // Compression + Translation
    let completed = 0;
    
    if (video.compression === 'COMPLETED') completed++;
    if (video.translation === 'COMPLETED' || video.translation === 'SKIPPED') completed++;
    
    return Math.round((completed / total) * 100);
  }, []);
  
  return (
    <VideoProcessingContext.Provider 
      value={{ 
        processingVideos,
        trackVideo,
        removeVideo,
        toggleExpanded,
        getCompletionPercentage
      }}
    >
      {children}
    </VideoProcessingContext.Provider>
  );
};